import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import './Register.css';

export default function Register() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    fname: '',
    lname: '',
    email: '',
    tel: '',
    address: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [telError, setTelError] = useState('');

  // ฟังก์ชันสำหรับทำความสะอาดเบอร์โทร (เก็บแค่ตัวเลข)
  const sanitizePhone = (value) => {
    return String(value || '').replace(/\D+/g, '');
  };

  // ฟังก์ชันสำหรับตรวจสอบเบอร์โทร (รองรับเฉพาะเบอร์มือถือ)
  const validatePhone = (phone) => {
    const cleaned = sanitizePhone(phone);
    if (!cleaned) {
      return { isValid: false, message: 'กรุณากรอกเบอร์โทรศัพท์' };
    }
    if (!cleaned.startsWith('0')) {
      return { isValid: false, message: 'เบอร์โทรศัพท์ต้องขึ้นต้นด้วย 0' };
    }
    // ตรวจสอบว่าเป็นตัวเลขทั้งหมด
    if (!/^\d+$/.test(cleaned)) {
      return { isValid: false, message: 'เบอร์โทรศัพท์ต้องเป็นตัวเลขเท่านั้น' };
    }
    
    // ตรวจสอบเฉพาะเบอร์มือถือ: 08x, 09x, 06x (10 หลัก)
    if (cleaned.startsWith('08') || cleaned.startsWith('09') || cleaned.startsWith('06')) {
      if (cleaned.length !== 10) {
        return { isValid: false, message: '' };
      }
      return { isValid: true, message: '' };
    }
    
    // เบอร์อื่นๆ ที่ไม่ใช่เบอร์มือถือ - ไม่แสดงข้อความ error
    return { isValid: false, message: '' };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // ถ้าเป็นช่องเบอร์โทร ให้ทำความสะอาดและตรวจสอบ
    if (name === 'tel') {
      processedValue = sanitizePhone(value);
      const validation = validatePhone(processedValue);
      if (processedValue && !validation.isValid) {
        setTelError(validation.message);
      } else {
        setTelError('');
      }
    }

    setFormData({
      ...formData,
      [name]: processedValue
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // ตรวจสอบเบอร์โทรก่อน submit
    const phoneValidation = validatePhone(formData.tel);
    if (!phoneValidation.isValid) {
      setTelError(phoneValidation.message);
      setError(phoneValidation.message);
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post('/api/customer/register', formData);
      const registeredEmail = response.data?.email || formData.email;
      setSuccess('สมัครสมาชิกสำเร็จ! กรุณายืนยันอีเมลของคุณ');
      navigate('/verify-email', {
        replace: true,
        state: {
          email: registeredEmail,
          fromRegister: true
        }
      });
    } catch (error) {
      console.error('Register error:', error);
      setError(error.response?.data?.error || 'สมัครสมาชิกไม่สำเร็จ');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="register-page">
      <section className="register-showcase" aria-hidden="true">
        <div className="register-showcase-overlay" />
        <div className="register-showcase-content">
          <h1>สมัครสมาชิก</h1>
          <p>
            สร้างบัญชีเพื่อสั่งซื้อสินค้า และติดตามคำสั่งซื้อ
          </p>
          <div className="register-showcase-meta">
            <div>
              <i className="bi bi-box-seam" />
              <span>สั่งซื้อสินค้าง่าย</span>
            </div>
            <div>
              <i className="bi bi-truck" />
              <span>ติดตามคำสั่งซื้อ</span>
            </div>
          </div>
        </div>
      </section>

      <section className="register-form-area">
        <div className="register-form-shell">
          <div className="register-brand-icons">
            <i className="bi bi-flower2" />
            <i className="bi bi-house-heart" />
          </div>

          <div className="register-card">
            <header className="register-card-header">
              <h2>สมัครสมาชิก</h2>
              <p>กรุณากรอกข้อมูลเพื่อสร้างบัญชีใหม่</p>
            </header>

            {error && (
              <div className="register-alert register-alert-error" role="alert">
                <span>{error}</span>
                <button
                  type="button"
                  className="register-alert-dismiss"
                  onClick={() => setError('')}
                  aria-label="ปิดข้อความข้อผิดพลาด"
                >
                  <i className="bi bi-x-lg" />
                </button>
              </div>
            )}

            {success && (
              <div className="register-alert register-alert-success" role="status">
                <span>{success}</span>
              </div>
            )}

            <form className="register-form" onSubmit={handleSubmit}>
              <div className="register-field">
                <label htmlFor="username">ชื่อผู้ใช้</label>
                <input
                  id="username"
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="สร้างชื่อผู้ใช้"
                  required
                  className="register-input"
                  autoComplete="username"
                />
              </div>

              <div className="register-field">
                <label htmlFor="password">รหัสผ่าน</label>
                <div className="register-password-input">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="ตั้งรหัสผ่าน"
                    required
                    className="register-input"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="register-input-toggle"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
                  >
                    <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
                  </button>
                </div>
              </div>

              <div className="register-field-grid">
                <div className="register-field">
                  <label htmlFor="fname">ชื่อจริง</label>
                  <input
                    id="fname"
                    type="text"
                    name="fname"
                    value={formData.fname}
                    onChange={handleChange}
                    placeholder="ชื่อ"
                    required
                    className="register-input"
                    autoComplete="given-name"
                  />
                </div>
                <div className="register-field">
                  <label htmlFor="lname">นามสกุล</label>
                  <input
                    id="lname"
                    type="text"
                    name="lname"
                    value={formData.lname}
                    onChange={handleChange}
                    placeholder="นามสกุล"
                    required
                    className="register-input"
                    autoComplete="family-name"
                  />
                </div>
              </div>

              <div className="register-field">
                <label htmlFor="tel">หมายเลขโทรศัพท์</label>
                <input
                  id="tel"
                  type="tel"
                  name="tel"
                  value={formData.tel}
                  onChange={handleChange}
                  placeholder="0812345678"
                  required
                  className={`register-input ${telError ? 'register-input--error' : ''}`}
                  autoComplete="tel"
                  maxLength={10}
                  pattern="0[689][0-9]{8}"
                  inputMode="numeric"
                />
                {telError && (
                  <span className="register-field-error" role="alert">
                    {telError}
                  </span>
                )}
              </div>

              <div className="register-field">
                <label htmlFor="email">อีเมล</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  required
                  className="register-input"
                  autoComplete="email"
                />
              </div>

              <div className="register-field">
                <label htmlFor="address">ที่อยู่จัดส่ง</label>
                <textarea
                  id="address"
                  name="address"
                  rows="3"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="กรอกที่อยู่สำหรับจัดส่งสินค้า"
                  required
                  className="register-input register-textarea"
                  autoComplete="street-address"
                />
              </div>
              <div className="register-secondary-actions">
                <button
                  type="button"
                  className="register-secondary-btn register-secondary-btn--outline"
                  onClick={() => navigate('/login')}
                >
                  มีบัญชีอยู่แล้ว?
                </button>
                <button
                  type="submit"
                  className="register-secondary-btn register-secondary-btn--primary"
                  disabled={isLoading}
                >
                  {isLoading ? 'กำลังสมัครสมาชิก...' : 'สมัครสมาชิก'}
                </button>
              </div>
            </form>
          </div>

        </div>
      </section>
    </div>
  );
}
